import { forwardRef, type ReactNode } from "react";

import type { colors } from "@eisox/tokens";

import { cn } from "../../index";
import CircleBase, { type CircleBaseProps } from "../base/CircleBase";
import { useCircleContext } from "../root/CircleContext";

const NAME = "Circle.Badge";

const variantMap: Record<string, { backgroundColor: keyof typeof colors.primary }> = {
  error: { backgroundColor: "red" },
  warning: { backgroundColor: "orange" },
  on: { backgroundColor: "green" },
  off: { backgroundColor: "darkBlue" },
  unknown: { backgroundColor: "darkGray" },
};

type Variant = keyof typeof variantMap;

export interface CircleBadgeProps extends Omit<CircleBaseProps, "size"> {
  children?: ReactNode;
  size?: number;
  variant?: Variant;
}

const CircleBadge = forwardRef<HTMLDivElement, CircleBadgeProps>(
  (
    { children, size: propSize, variant, backgroundColor = "blue", color = "white", className, style, ...props },
    ref,
  ) => {
    const { size: circleSize, borderRadius } = useCircleContext(NAME);

    const badgeX = circleSize - borderRadius + borderRadius * Math.cos(Math.PI / 4);
    const badgeY = borderRadius - borderRadius * Math.sin(Math.PI / 4);

    const badgeSize = propSize ?? circleSize / 2.5;
    const borderSize = (propSize ?? circleSize) / 20;

    const variantColors = variant ? variantMap[variant] : undefined;

    return (
      <CircleBase
        {...props}
        ref={ref}
        size={badgeSize}
        backgroundColor={variantColors?.backgroundColor ?? backgroundColor}
        color={color}
        className={cn("z-1 overflow-hidden border-solid border-primary-white", className)}
        style={{
          ...style,
          position: "absolute",
          top: badgeY,
          left: badgeX,
          transform: "translate(-50%, -50%)",
          borderWidth: borderSize,
        }}
      >
        {children}
      </CircleBase>
    );
  },
);

CircleBadge.displayName = NAME;

export default CircleBadge;
